import * as React from 'react'
import { CheckCircle2, ChevronDown, Loader2, RefreshCw, Save } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiJson } from '@/lib/api'
import { HelpHint } from '@/components/ui/help-hint'
import type { ScriptDetail, ScriptScene, ScriptVersion } from '@/types'

interface Props {
  scriptId: number
  onApproved: () => void
  /** Reports unsaved-edit state up to the wizard host so it can confirm
   *  before navigating away (Task 6). The host stores the latest value in
   *  a ref and checks it inside goTo(). */
  onDirtyChange?: (dirty: boolean) => void
}

function versionLabel(v: ScriptVersion): string {
  return `v${v.version_number} — ${v.source}`
}

type ScriptProvider = 'gemini' | 'openrouter'

const DEFAULT_PROVIDER: ScriptProvider = 'gemini'

/** Read provider from a version's persisted meta, falling back to the default. */
function providerFromMeta(meta: Record<string, unknown> | null | undefined): ScriptProvider {
  if (!meta) return DEFAULT_PROVIDER
  const v = meta.provider
  return v === 'gemini' || v === 'openrouter' ? v : DEFAULT_PROVIDER
}

/** Format cost cents — sub-cent precision, rounds up at $1. */
function formatScriptCost(cents: number | null | undefined): string | null {
  if (cents == null || !Number.isFinite(cents)) return null
  if (cents <= 0) return '0¢'
  if (cents < 1) return `${cents.toFixed(2)}¢`
  if (cents < 100) return `${cents.toFixed(1)}¢`
  return `$${(cents / 100).toFixed(2)}`
}

export function StepScript({ scriptId, onApproved, onDirtyChange }: Props) {
  const [script, setScript] = React.useState<ScriptDetail | null>(null)
  const [loadingScript, setLoadingScript] = React.useState(true)
  const [activeVersionId, setActiveVersionId] = React.useState<number | null>(null)

  // Editable scene state (derived from active version)
  const [editHook, setEditHook] = React.useState('')
  const [editCta, setEditCta] = React.useState('')
  const [editScenes, setEditScenes] = React.useState<ScriptScene[]>([])
  const [dirty, setDirty] = React.useState(false)

  // Bubble dirty state up so the host wizard can confirm before navigating
  // away with unsaved edits (Task 6). Resets on unmount so the host doesn't
  // keep blocking nav after the user leaves the script step entirely.
  React.useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])

  // Also protect against tab close / refresh while the editor is dirty.
  // (BrowserRouter means we can't use useBlocker for in-SPA browser-back,
  // but beforeunload still covers the "I'll close this tab" case.)
  React.useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  // Action states
  const [saving, setSaving] = React.useState(false)
  const [regenerating, setRegenerating] = React.useState(false)
  const [approving, setApproving] = React.useState(false)
  const [regenNotes, setRegenNotes] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  // Per-scene regenerating: Set of scene_number values currently in-flight
  const [regeneratingScenes, setRegeneratingScenes] = React.useState<Set<number>>(new Set())

  const fetchScript = React.useCallback(async () => {
    try {
      const data = await apiJson<ScriptDetail>(`/api/v1/scripts/${scriptId}`)
      setScript(data)
      const vid = data.active_version_id ?? data.versions[0]?.id ?? null
      setActiveVersionId(vid)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoadingScript(false)
    }
  }, [scriptId])

  React.useEffect(() => {
    fetchScript()
  }, [fetchScript])

  // Sync editor fields when active version changes
  const activeVersion = React.useMemo(
    () => script?.versions.find((v) => v.id === activeVersionId) ?? null,
    [script, activeVersionId],
  )

  React.useEffect(() => {
    if (!activeVersion) return
    setEditHook(activeVersion.hook ?? '')
    setEditCta(activeVersion.cta ?? '')
    setEditScenes(activeVersion.scenes.map((s) => ({ ...s })))
    setDirty(false)
  }, [activeVersion])

  const handleSceneChange = (
    idx: number,
    field: keyof ScriptScene,
    value: string | number,
  ) => {
    setEditScenes((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      return next
    })
    setDirty(true)
  }

  const handleSceneDurationChange = async (idx: number, newDuration: number) => {
    const scene = editScenes[idx]
    const prevDuration = scene.duration_seconds
    // Optimistic update so Select reflects new value immediately
    handleSceneChange(idx, 'duration_seconds', newDuration)

    setRegeneratingScenes((prev) => new Set(prev).add(scene.scene_number))
    setError(null)
    try {
      const updated = await apiJson<ScriptScene>(
        `/api/v1/scripts/${scriptId}/scenes/${scene.scene_number}/regenerate`,
        {
          method: 'POST',
          body: JSON.stringify({
            duration_seconds: newDuration,
            // Inherit the provider that originally generated this version so
            // single-scene regens stay consistent with the rest of the script.
            model_provider: providerFromMeta(activeVersion?.meta),
          }),
        },
      )
      setEditScenes((prev) => {
        const next = [...prev]
        next[idx] = updated
        return next
      })
      setDirty(true)
    } catch (err) {
      setError(`Scene ${scene.scene_number} regenerate failed: ${(err as Error).message}`)
      // Revert duration on failure
      setEditScenes((prev) => {
        const next = [...prev]
        next[idx] = { ...next[idx], duration_seconds: prevDuration }
        return next
      })
    } finally {
      setRegeneratingScenes((prev) => {
        const next = new Set(prev)
        next.delete(scene.scene_number)
        return next
      })
    }
  }

  const handleSave = async () => {
    if (!activeVersionId) return
    setSaving(true)
    setError(null)
    try {
      const data = await apiJson<ScriptDetail>(
        `/api/v1/scripts/${scriptId}/versions/${activeVersionId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            hook: editHook || undefined,
            cta: editCta || undefined,
            scenes: editScenes,
          }),
        },
      )
      setScript(data)
      setActiveVersionId(data.active_version_id)
      setDirty(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const handleRegenerate = async () => {
    setRegenerating(true)
    setError(null)
    try {
      const data = await apiJson<ScriptDetail>(
        `/api/v1/scripts/${scriptId}/regenerate`,
        {
          method: 'POST',
          body: JSON.stringify({
            notes: regenNotes.trim() || undefined,
            // Inherit the provider from the active version — keeps the new
            // version comparable to the one we're regenerating from.
            model_provider: providerFromMeta(activeVersion?.meta),
          }),
        },
      )
      setScript(data)
      setActiveVersionId(data.active_version_id)
      setRegenNotes('')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setRegenerating(false)
    }
  }

  const handleApprove = async () => {
    setApproving(true)
    setError(null)
    try {
      await apiJson<ScriptDetail>(`/api/v1/scripts/${scriptId}/approve`, {
        method: 'POST',
      })
      onApproved()
    } catch (err) {
      setError((err as Error).message)
      setApproving(false)
    }
  }

  if (loadingScript) {
    return (
      <div className="flex items-center justify-center py-24" aria-label="Loading script">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!script || !activeVersion) {
    return (
      <p className="text-destructive text-sm" role="alert">
        {error ?? 'Script not found.'}
      </p>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{script.title ?? 'Script'}</h2>
          <p className="text-sm text-muted-foreground">
            Review and edit your script, then approve to continue.
          </p>
          {(() => {
            // Surface provider + cost from the active version's meta so the
            // user sees what they're spending. Both fields are populated by
            // script_service.generate_script (Phase 3).
            const meta = activeVersion?.meta as
              | { provider?: string; model?: string; cost_cents?: number; generation_time_seconds?: number }
              | null
              | undefined
            if (!meta?.provider) return null
            const cost = formatScriptCost(meta.cost_cents ?? null)
            const time =
              typeof meta.generation_time_seconds === 'number'
                ? `${meta.generation_time_seconds.toFixed(1)}s`
                : null
            const parts = [
              meta.model || meta.provider,
              cost,
              time,
            ].filter((p): p is string => Boolean(p))
            return (
              <p className="text-xs text-muted-foreground/70 mt-1 tabular-nums">
                {parts.join(' · ')}
              </p>
            )
          })()}
        </div>

        {/* Version selector */}
        {script.versions.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Active version</span>
            <Select
              value={String(activeVersionId)}
              onValueChange={(v) => setActiveVersionId(Number(v))}
            >
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {script.versions.map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {versionLabel(v)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Hook & CTA */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Hook &amp; CTA
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="hook">Hook</Label>
              <HelpHint text="The opening line or visual moment that stops the scroll. Edit if the generated hook doesn't grab attention fast enough." />
            </div>
            <Input
              id="hook"
              value={editHook}
              onChange={(e) => {
                setEditHook(e.target.value)
                setDirty(true)
              }}
              placeholder="Opening hook line..."
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cta">CTA</Label>
              <HelpHint text="Closing call-to-action. Edit if you want a different ask or link text." />
            </div>
            <Input
              id="cta"
              value={editCta}
              onChange={(e) => {
                setEditCta(e.target.value)
                setDirty(true)
              }}
              placeholder="Call to action..."
            />
          </div>
        </CardContent>
      </Card>

      {/* Scenes */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Scenes ({editScenes.length})
        </h3>
        {editScenes.map((scene, idx) => {
          const isSceneRegen = regeneratingScenes.has(scene.scene_number)
          return (
            <Card key={scene.scene_number}>
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="tabular-nums">
                    Scene {scene.scene_number}
                  </Badge>
                  {isSceneRegen ? (
                    <div className="flex items-center gap-1.5 ml-auto text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Rewriting for {scene.duration_seconds}s…</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 ml-auto">
                      <HelpHint text="Duration of this single scene. Changing this regenerates the scene prompt via AI to fit the new timing. Only 5, 10, or 15 are supported by Seedance." side="right" />
                      <Select
                        value={String(scene.duration_seconds)}
                        onValueChange={(v) => handleSceneDurationChange(idx, Number(v))}
                      >
                        <SelectTrigger className="w-20 h-7 text-xs" aria-label={`Scene ${scene.scene_number} duration`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[5, 10, 15].map((d) => (
                            <SelectItem key={d} value={String(d)}>
                              {d}s
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <HelpHint text="Human-readable summary of the scene for your review. The AI doesn't use this to generate — it uses the Prompt field below." />
                  </div>
                  <Textarea
                    value={scene.description}
                    onChange={(e) => handleSceneChange(idx, 'description', e.target.value)}
                    rows={2}
                    className="resize-none text-sm"
                    disabled={isSceneRegen}
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs text-muted-foreground">Prompt</Label>
                    <HelpHint text="This is the actual text sent to Seedance 2.0. Follow the 6-step formula: Subject + Action + Environment + Camera + Style + Constraints. 60-100 words." />
                  </div>
                  <Textarea
                    value={scene.prompt}
                    onChange={(e) => handleSceneChange(idx, 'prompt', e.target.value)}
                    rows={3}
                    className="resize-none text-sm"
                    disabled={isSceneRegen}
                  />
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Action bar */}
      <Card className="border-dashed">
        <CardContent className="pt-4 space-y-4">
          {/* Regenerate with notes */}
          <div className="space-y-2">
            <Label htmlFor="regen-notes" className="text-xs text-muted-foreground">
              Steering notes for regeneration (optional)
            </Label>
            <Textarea
              id="regen-notes"
              placeholder="e.g. Make it more upbeat, focus on product close-ups..."
              value={regenNotes}
              onChange={(e) => setRegenNotes(e.target.value)}
              rows={2}
              className="resize-none text-sm"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={!dirty || saving}
              className="flex-1"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" /> Edit &amp; Save
                </>
              )}
            </Button>

            <Button
              variant="outline"
              onClick={handleRegenerate}
              disabled={regenerating}
              className="flex-1"
            >
              {regenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" /> Regenerate
                </>
              )}
            </Button>

            <Button
              onClick={handleApprove}
              disabled={approving}
              className="flex-1"
            >
              {approving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" /> Approve &amp; Continue
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
